import { useTrack } from '@sortlist-frontend/tracking';
import { TrackingParamsCpc } from '@sortlist-frontend/tracking/legacy';
import { useRadarTracker } from '@sortlist-frontend/user-tools';
import { uuidv4 } from '@sortlist-frontend/utils';

import { NavigationData } from '_components/layout/Layout';
import { useTrackWithoutContext } from '_core/repos/track.repo';
import { AgencyWebsiteRedirectParams } from '_types/generic';
import { Agency, PagesAgency } from '_types/public-api';

interface TrackAgencyCardClickProps {
  agency: Agency;
  data: PagesAgency;
  navigationData: NavigationData;
  paid?: boolean;
  relativePosition: number;
  absolutePosition: number;
}

export function useTrackAgencyCardClick(props: TrackAgencyCardClickProps) {
  const { data, paid, navigationData, relativePosition, absolutePosition } = props;

  const { origin, locale } = navigationData;

  const agency = props?.agency?.attributes;

  const { mutateAsync: internalTrack } = useTrackWithoutContext(origin);
  const { track } = useTrack({ app: 'public', locale });
  const { track: radarTrack } = useRadarTracker();

  const trackOpenWebsite = () => {
    track({
      slug: 'websiteOpened',
      data: {
        agencyUuid: agency.external_id,
        agencySlug: agency.slug,
        page: 'longtail',
        pageType: 'longtail',
        position: relativePosition,
        cardPosition: relativePosition,
        absolutePosition,
      },
    });
    radarTrack({
      eventName: 'websiteVisited',
      properties: {
        agency_slug: agency.slug,
      },
    });
  };

  const trackAgencyProfile = async ({
    internal,
    external,
    openWebsite = false,
  }: {
    internal: boolean;
    external: boolean;
    openWebsite?: boolean;
  }) => {
    const trackingEventUUID = uuidv4();
    // call internal tracking
    const trackData: TrackingParamsCpc = {
      name: 'agencyCardClicked',
      url: location.href,
      agency_external_id: agency.external_id,
      position: paid ? relativePosition : (data?.attributes?.position ?? 0) + 1,
      result_type: data?.attributes?.section?.toLowerCase(),
      cta: 'card',
      tracking_event_uuid: trackingEventUUID,
    };

    internal && internalTrack(trackData);
    if (external) {
      track({
        slug: 'agencyCardClicked',
        data: {
          agencyUuid: agency.external_id,
          agencySlug: agency.slug,
          trackingEventUUID,
          // attributes.position starts at 0
          position: paid ? relativePosition : (data?.attributes?.position ?? 0) + 1,
          paid,
          absolutePosition,
          cardPosition: relativePosition,
          openWebsite,
          pageType: 'longtail',
          pageSection: paid ? 'paid' : 'organic',
          pageId: data?.relationships?.page?.data?.id,
        },
      });

      if (openWebsite) {
        trackOpenWebsite();
      }
    }
  };

  return {
    trackAgencyProfile,
  };
}

export const buildRedirectUrlParams = (props: {
  agency: Agency;
  data: PagesAgency;
  paid?: boolean;
  position: number;
  navigationData: NavigationData;
}): string => {
  const { paid, position, data, navigationData } = props;
  const agency = props?.agency?.attributes;
  const { origin, resolvedUrl } = navigationData;

  const params: AgencyWebsiteRedirectParams = {
    url: location.href,
    agency_external_id: agency.external_id,
    position: paid ? position : (data?.attributes?.position ?? 0) + 1,
    result_type: data?.attributes?.section?.toLowerCase(),
    website_url: agency?.website_url ?? null,
    campaign_url: `${origin}${resolvedUrl}`,
  };

  const urlParams = new URLSearchParams();
  const encoded = btoa(encodeURIComponent(JSON.stringify(params)));
  urlParams.set('redirectParams', encoded);

  return urlParams.toString();
};
